import { useEffect } from 'react';
import { useStore } from '@/state/store';
import { Segmented } from '@/components/ui/Segmented';
import { TOOLS, TOOL_ORDER, toolForPath } from '@/tools/registry';
import { saveTool } from '@/state/persistence';
import type { ToolId } from '@/types';

/**
 * Always visible, so the other two tools are one click away and never feel
 * hidden behind a menu. Switching also rewrites the address bar, which keeps
 * the visible tool and the URL — and its metadata — telling the same story.
 */
export function ToolSwitcher() {
  const tool = useStore((s) => s.tool);
  const setTool = useStore((s) => s.setTool);
  const setUi = useStore((s) => s.setUi);

  // The back button should move between tools rather than leave the studio.
  useEffect(() => {
    const onPop = () => {
      const next = toolForPath(location.pathname);
      if (next) {
        setTool(next);
        setUi({ tab: TOOLS[next].defaultTab });
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [setTool, setUi]);

  const change = (next: ToolId) => {
    if (next === tool) return;
    setTool(next);
    setUi({ tab: TOOLS[next].defaultTab });
    saveTool(next);
    history.pushState(null, '', TOOLS[next].path);
  };

  return (
    <Segmented<ToolId>
      size="sm"
      value={tool}
      onChange={change}
      options={TOOL_ORDER.map((id) => {
        const Icon = TOOLS[id].icon;
        return {
          value: id,
          title: TOOLS[id].blurb,
          label: (
            <>
              <Icon size={13} />
              <span className="hidden lg:inline">{TOOLS[id].label}</span>
            </>
          ),
        };
      })}
    />
  );
}
